import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';

import config from '../../../config.js';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import styles from '../../../styles/partial/modal/ImportSonglistModal.module.css';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { serverMessage } from '../../../_helpers/errorMessage.js';
import { withoutProviderName } from '../../../_helpers/manualSongs.js';

/**
 * MIME types browsers and operating systems report for a .csv file
 * (ENG-512). An empty type is common too; the extension is always required.
 */
export const CSV_MIME_TYPES = Object.freeze([
  'text/csv',
  'application/csv',
  'text/comma-separated-values',
  'application/vnd.ms-excel',
  '',
]);

/** The backend's upload limit (csvimport maxUploadBytes, 50 MB). */
export const MAX_CSV_BYTES = 50 * 1024 * 1024;

const STAGE = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  ERROR: 'error',
};


const getFocusableElements = (container) => {
  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
};

const ImportSonglistModal = ({ isOpen, onClose }) => {
  const { bandid } = useParams();
  const { apiBaseUrl } = config;
  const fetchWrapper = useFetchWrapper();
  const navigate = useNavigate();
  const t = useT();

  const [stage, setStage] = useState(STAGE.IDLE);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const backdropRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadActiveRef = useRef(false);

  // Reset state on open; on close, cancel any in-flight upload
  useEffect(() => {
    if (!isOpen) {
      uploadActiveRef.current = false;
      setStage(STAGE.IDLE);
      return;
    }
    setStage(STAGE.IDLE);
    setSelectedFile(null);
    setIsDragging(false);
    setErrorMessage('');
  }, [isOpen]);

  // Only move focus when the modal opens, not on every stage transition
  useEffect(() => {
    if (!isOpen || !backdropRef.current) return;
    const focusable = getFocusableElements(backdropRef.current);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      backdropRef.current.focus();
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    uploadActiveRef.current = false;
    onClose();
  }, [onClose]);

  const handleFileChange = useCallback((file) => {
    if (!file) return;
    const hasCsvExtension = file.name.toLowerCase().endsWith('.csv');
    const hasCsvMime = CSV_MIME_TYPES.includes(file.type);
    if (!hasCsvExtension || !hasCsvMime) {
      setErrorMessage(t('importSonglist.modal.errors.notCsv'));
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setErrorMessage(t('importSonglist.modal.errors.tooLarge', { max: 50 }));
      setSelectedFile(null);
      return;
    }
    setErrorMessage('');
    setSelectedFile(file);
  }, [t]);

  const handleInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFileChange(file);
  }, [handleFileChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange(file);
  }, [handleFileChange]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setStage(STAGE.UPLOADING);
    setErrorMessage('');
    uploadActiveRef.current = true;

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const result = await fetchWrapper.post(
        `${apiBaseUrl}/bands/${bandid}/import/songs/csv`,
        formData
      );
      if (!uploadActiveRef.current) return;
      if (!result.job_id) {
        setErrorMessage(t('importSonglist.modal.errors.noJobId'));
        setStage(STAGE.ERROR);
        return;
      }
      onClose();
      navigate(`/performers/${bandid}/import-songs?job_id=${result.job_id}`);
    } catch (err) {
      if (!uploadActiveRef.current) return;
      // The server's reason when it gave one ("File too large", a bad
      // header, an import already running), not always "Upload failed".
      // Never one that names the song lookup's provider (ENG-796).
      setErrorMessage(withoutProviderName(serverMessage(err)) || t('importSonglist.modal.errors.uploadFailed'));
      setStage(STAGE.ERROR);
    }
  }, [selectedFile, fetchWrapper, apiBaseUrl, bandid, navigate, onClose, t]);

  const handleRetry = useCallback(() => {
    setStage(STAGE.IDLE);
    setSelectedFile(null);
    setErrorMessage('');
  }, []);

  const handleBackdropKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
      return;
    }
    if (e.key === 'Tab' && backdropRef.current) {
      const focusable = getFocusableElements(backdropRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }, [handleClose]);

  if (!isOpen) return null;

  const titleId = 'import-songlist-modal-title';

  const modal = (
    <div
      ref={backdropRef}
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex="-1"
      onClick={handleClose}
      onKeyDown={handleBackdropKeyDown}
    >
      <div
        className={styles.panel}
        data-testid="modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeBtn}
          aria-label={t('importSonglist.modal.close')}
          onClick={handleClose}
        >
          ×
        </button>

        <div className={styles.header}>
          <span className={styles.kicker}>{t('importSonglist.modal.kicker')}</span>
          <h2 id={titleId} className={styles.title}>{t('importSonglist.modal.title')}</h2>
          <p className={styles.subtitle}>
            {t('importSonglist.modal.subtitle')}
          </p>
        </div>

        <div className={styles.body}>
          {errorMessage && (
            <p className={styles.errorMsg} role="alert" data-testid="import-modal-error">{errorMessage}</p>
          )}

          {stage === STAGE.IDLE && (
            <>
              <div
                className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex="0"
                aria-label={
                  selectedFile
                    ? t('importSonglist.modal.dropZoneSelected', { fileName: selectedFile.name })
                    : t('importSonglist.modal.dropZoneLabel')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  data-testid="csv-file-input"
                  className={styles.fileInput}
                  onChange={handleInputChange}
                  tabIndex="-1"
                />
                {selectedFile ? (
                  <p className={styles.fileName}>{selectedFile.name}</p>
                ) : (
                  <>
                    <p className={styles.dropZoneText}>{t('importSonglist.modal.dropZoneText')}</p>
                    <p className={styles.dropZoneHint}>{t('importSonglist.modal.dropZoneHint')}</p>
                  </>
                )}
              </div>
              <button
                type="button"
                className={styles.uploadBtn}
                onClick={handleUpload}
                disabled={!selectedFile}
                data-testid="csv-upload"
              >
                {t('importSonglist.modal.upload')}
              </button>
            </>
          )}

          {stage === STAGE.UPLOADING && (
            <div className={styles.connectingState} role="status">
              <div className={styles.spinner} aria-hidden="true" />
              <p className={styles.connectingLabel}>{t('importSonglist.modal.uploading')}</p>
              <p className={styles.connectingHint}>
                {t('importSonglist.modal.uploadingHint')}
              </p>
            </div>
          )}

          {stage === STAGE.ERROR && (
            <button
              type="button"
              className={styles.uploadBtn}
              onClick={handleRetry}
            >
              {t('common.actions.tryAgain')}
            </button>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleClose}
          >
            {t('common.actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ImportSonglistModal;
